import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Star, Quote, CheckCircle2, Loader2, PenLine } from "lucide-react";
import { Reveal, trackEvent } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-obsidian/80 px-4 py-3.5 text-sm text-slate-100 placeholder:text-slate-500 outline-none transition-colors duration-200 focus:border-ember/60 focus:ring-2 focus:ring-ember/20";

const Testimonials = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ parent_name: "", email: "", rating: 5, message: "", consent: false });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/testimonials`, {
        parent_name: form.parent_name,
        email: form.email || null,
        rating: form.rating,
        message: form.message,
        consent: form.consent,
      });
      setSent(true);
      trackEvent("testimonial_submit", { rating: form.rating });
      toast.success("Merci ! Votre témoignage sera publié après relecture.");
    } catch (err) {
      toast.error("Une erreur est survenue. Réessayez dans un instant.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="temoignages" className="py-24 sm:py-32" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.25em] text-ember mb-4">Témoignages</p>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold uppercase tracking-tight text-slate-100 max-w-2xl leading-tight">
            Ils me recommandent
          </h2>
          <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
            Les retours des parents et des apprenants accompagnés — publiés après relecture,
            avec leur accord.
          </p>
        </Reveal>

        {items.length > 0 && (
          <div className="mt-12 grid gap-5 md:grid-cols-3">
            {items.map((t, i) => (
              <Reveal key={t.id || i} delay={i * 0.1}>
                <figure data-testid={`testimonial-card-${i}`} className="h-full rounded-2xl border border-white/10 bg-coal p-7 flex flex-col">
                  <Quote size={26} className="text-ember/70" />
                  <blockquote className="mt-4 flex-1 text-sm sm:text-base text-slate-300 leading-relaxed whitespace-pre-line">« {t.message} »</blockquote>
                  <figcaption className="mt-6 pt-5 border-t border-white/10">
                    <div className="flex gap-1 mb-2">
                      {Array.from({ length: t.rating || 5 }).map((_, s) => (
                        <Star key={s} size={14} className="text-gold" fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-xs sm:text-sm font-semibold text-slate-200">{t.parent_name}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}

        <Reveal delay={0.1}>
          <div className="mt-12 rounded-3xl border border-white/10 bg-coal p-7 sm:p-9">
            {sent ? (
              <div className="py-10 text-center" data-testid="testimonial-success">
                <CheckCircle2 size={48} className="mx-auto text-gold" />
                <h3 className="mt-5 font-heading text-2xl font-extrabold uppercase text-slate-100">Merci pour votre confiance</h3>
                <p className="mt-3 text-sm sm:text-base text-slate-400 max-w-md mx-auto leading-relaxed">
                  Votre témoignage a bien été transmis à Yassine. Il sera publié ici après relecture.
                </p>
              </div>
            ) : showForm ? (
              <form onSubmit={submit} data-testid="testimonial-form" className="space-y-5">
                <div className="flex items-center gap-3 mb-1">
                  <PenLine size={18} className="text-gold" />
                  <h3 className="font-heading text-xl font-bold uppercase text-slate-100">Partager votre expérience</h3>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="testimonial-name" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Votre nom (ex. Sophie M.) *
                    </label>
                    <input id="testimonial-name" data-testid="testimonial-name-input" required value={form.parent_name} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className={inputCls} placeholder="Prénom et initiale suffisent" />
                  </div>
                  <div>
                    <label htmlFor="testimonial-email" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                      Email (non publié)
                    </label>
                    <input id="testimonial-email" data-testid="testimonial-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} placeholder="Optionnel" />
                  </div>
                </div>
                <div>
                  <span className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Votre note</span>
                  <div className="flex gap-1.5" data-testid="testimonial-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        data-testid={`rating-star-${n}`}
                        onClick={() => setForm({ ...form, rating: n })}
                        className="p-1 transition-transform duration-150 hover:scale-110"
                        aria-label={`${n} étoile${n > 1 ? "s" : ""}`}
                      >
                        <Star size={26} className={n <= form.rating ? "text-gold" : "text-slate-600"} fill={n <= form.rating ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="testimonial-message" className="block text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
                    Votre témoignage *
                  </label>
                  <textarea id="testimonial-message" data-testid="testimonial-message-input" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} resize-none`} placeholder="Racontez en quelques lignes ce que l'accompagnement a changé…" />
                </div>
                <label className="flex items-start gap-3 cursor-pointer" data-testid="testimonial-consent">
                  <input type="checkbox" required checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1 w-4 h-4 accent-[#E63946]" />
                  <span className="text-xs text-slate-400 leading-relaxed">
                    J'accepte que ce témoignage soit publié sur le site après relecture par Yassine.
                  </span>
                </label>
                <button type="submit" data-testid="testimonial-submit-button" disabled={sending} className="inline-flex items-center justify-center gap-2 rounded-full bg-ember hover:bg-emberdark disabled:opacity-60 px-7 py-4 text-sm font-bold uppercase tracking-wide transition-colors duration-200">
                  {sending && <Loader2 size={17} className="animate-spin" />}
                  {sending ? "Envoi en cours…" : "Envoyer mon témoignage"}
                </button>
              </form>
            ) : (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl font-bold uppercase text-slate-100">
                    Vous avez été accompagné ? Votre avis compte.
                  </h3>
                  <p className="mt-2 text-sm text-slate-400 max-w-lg leading-relaxed">
                    Recommandez le professionnalisme de Yassine en quelques lignes — votre
                    témoignage aide d'autres familles à franchir le pas. Publié après relecture.
                  </p>
                </div>
                <button
                  data-testid="testimonial-open-form-button"
                  onClick={() => setShowForm(true)}
                  className="shrink-0 inline-flex items-center gap-2 rounded-full bg-ember hover:bg-emberdark px-6 py-3.5 text-xs font-bold uppercase tracking-wide transition-colors duration-200"
                >
                  <PenLine size={15} />
                  Laisser un témoignage
                </button>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Testimonials;
